import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, TrendingUp, Clock, CheckCircle, Users, ArrowUpRight, Target, Award, ShieldCheck, ShieldAlert, Pencil, Check, X, Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const fmtCur = (n) => `AED ${(n || 0).toLocaleString()}`;
const months = () => {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push({ value: d.toISOString().slice(0, 7), label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) });
    }
    return opts;
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'text-primary', bgColor = 'bg-primary/10' }) => (
    <Card>
        <CardContent className="p-4">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
                    <p className={`text-2xl font-bold font-mono mt-1 ${color}`}>{value}</p>
                    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
                <div className={`p-2.5 rounded-xl ${bgColor}`}><Icon className={`h-5 w-5 ${color}`} /></div>
            </div>
        </CardContent>
    </Card>
);

const ScatterChart = ({ data, agentName }) => {
    if (!data?.length) return null;
    const maxVal = Math.max(...data.map(d => d.net_pay), 1);
    const h = 220, w = 600, pad = { t: 20, r: 20, b: 40, l: 60 };
    const plotW = w - pad.l - pad.r, plotH = h - pad.t - pad.b;

    const points = (key, color) => data.map((d, i) => {
        const x = pad.l + (i / Math.max(data.length - 1, 1)) * plotW;
        const y = pad.t + plotH - (d[key] / maxVal) * plotH;
        return { x, y, val: d[key], label: d.label };
    });

    const commPts = points('commission', '#10b981');
    const payPts = points('net_pay', '#3b82f6');
    const pathStr = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4" /> {agentName || 'Commission'} — Monthly Trend</CardTitle>
            </CardHeader>
            <CardContent>
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 240 }}>
                    {/* Grid */}
                    {[0, 0.25, 0.5, 0.75, 1].map(f => (
                        <g key={f}>
                            <line x1={pad.l} y1={pad.t + plotH * (1 - f)} x2={w - pad.r} y2={pad.t + plotH * (1 - f)} stroke="currentColor" strokeOpacity={0.08} />
                            <text x={pad.l - 8} y={pad.t + plotH * (1 - f) + 4} textAnchor="end" fill="currentColor" fillOpacity={0.4} fontSize={9}>{Math.round(maxVal * f).toLocaleString()}</text>
                        </g>
                    ))}
                    {/* Lines */}
                    <path d={pathStr(payPts)} fill="none" stroke="#3b82f6" strokeWidth={2.5} strokeLinejoin="round" />
                    <path d={pathStr(commPts)} fill="none" stroke="#10b981" strokeWidth={2.5} strokeLinejoin="round" strokeDasharray="6,3" />
                    {/* Dots */}
                    {payPts.map((p, i) => <circle key={`p${i}`} cx={p.x} cy={p.y} r={4} fill="#3b82f6" />)}
                    {commPts.map((p, i) => <circle key={`c${i}`} cx={p.x} cy={p.y} r={4} fill="#10b981" />)}
                    {/* Labels */}
                    {data.map((d, i) => {
                        const x = pad.l + (i / Math.max(data.length - 1, 1)) * plotW;
                        return <text key={i} x={x} y={h - 8} textAnchor="middle" fill="currentColor" fillOpacity={0.5} fontSize={9}>{d.label}</text>;
                    })}
                    {/* Legend */}
                    <circle cx={pad.l + 10} cy={h - 28} r={4} fill="#3b82f6" /><text x={pad.l + 18} y={h - 25} fill="currentColor" fillOpacity={0.6} fontSize={9}>Net Pay (Salary + Commission)</text>
                    <circle cx={pad.l + 200} cy={h - 28} r={4} fill="#10b981" /><text x={pad.l + 208} y={h - 25} fill="currentColor" fillOpacity={0.6} fontSize={9}>Commission Only</text>
                </svg>
            </CardContent>
        </Card>
    );
};

export default function CommissionDashboard() {
    const { user } = useAuth();
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [scatterData, setScatterData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [drillDept, setDrillDept] = useState(null);
    const [drillData, setDrillData] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [approving, setApproving] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [txnFilter, setTxnFilter] = useState('all'); // commission_type filter
    const [selectedTxnIds, setSelectedTxnIds] = useState(new Set());
    const [txnLoading, setTxnLoading] = useState(false);
    const [editingTxn, setEditingTxn] = useState(null);
    const [editCommission, setEditCommission] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [generating, setGenerating] = useState(false);
    const [myTxns, setMyTxns] = useState([]);
    const [agentDetail, setAgentDetail] = useState(null); // { agent_id, agent_name }
    const [agentDetailData, setAgentDetailData] = useState(null);
    const [agentDetailLoading, setAgentDetailLoading] = useState(false);
    const [ceoPool, setCeoPool] = useState(null);
    const monthOpts = months();

    const pendingCount = useMemo(() => transactions.filter(t => t.status === 'pending').length, [transactions]);
    const approvedCount = useMemo(() => transactions.filter(t => t.status === 'approved').length, [transactions]);

    const isCEO = ['super_admin', 'coo'].includes(user?.role);
    const isTL = user?.role === 'team_leader';
    const isSales = ['sales_executive', 'team_leader'].includes(user?.role);
    const isCS = ['cs_agent', 'cs_head'].includes(user?.role);
    const isCSHead = user?.role === 'cs_head';
    const isBD = ['business_development_manager', 'business_development_manager_', 'business_development', 'bd_executive'].includes(user?.role);
    const [payoutData, setPayoutData] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, [month]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const promises = [
                apiClient.get(`/commissions/dashboard?month=${month}`),
                apiClient.get(`/commissions/scatter-data?months=6`),
                apiClient.get(`/commissions/ceo-pool?month=${month}`).catch(() => ({ data: null })),
            ];
            if (!isCEO) {
                promises.push(apiClient.get(`/commissions/transactions?month=${month}`));
                promises.push(apiClient.get(`/commissions/my-payout?month=${month}`));
            }
            const results = await Promise.all(promises);
            setData(results[0].data);
            setScatterData(results[1].data);
            setCeoPool(results[2]?.data || null);
            if (results[3]) setMyTxns(results[3].data.transactions || []);
            if (results[4]) setPayoutData(results[4].data);
        } catch (e) { toast.error('Failed to load commission data'); logger.error(e); }
        finally { setLoading(false); }
    };

    const openDrill = async (dept) => {
        setDrillDept(dept);
        setDrillLoading(true);
        try {
            const res = await apiClient.get(`/commissions/ceo/drill?dept=${dept}&month=${month}`);
            setDrillData(res.data);
        } catch { toast.error('Failed to load drill data'); }
        finally { setDrillLoading(false); }
    };

    const openAgentDetail = async (agentId, agentName) => {
        setAgentDetail({ agent_id: agentId, agent_name: agentName });
        setAgentDetailData(null);
        setAgentDetailLoading(true);
        try {
            const res = await apiClient.get(`/commissions/agent-details/${agentId}?month=${month}`);
            setAgentDetailData(res.data);
        } catch { toast.error('Failed to load agent commission details'); }
        finally { setAgentDetailLoading(false); }
    };

    const handleApproval = async (department, action) => {
        setApproving(true);
        try {
            await apiClient.post('/commissions/approve', { department, month, action });
            toast.success(`${department.toUpperCase()} commissions ${action === 'approve' ? 'approved' : 'revoked'} for ${monthOpts.find(o => o.value === month)?.label}`);
            fetchData();
        } catch (e) { toast.error('Failed to update approval'); }
        finally { setApproving(false); }
    };

    const getApprovalStatus = (dept) => data?.approvals?.[dept]?.status === 'approved';
    const userApprovalStatus = data?.approval_status;

    // Map UI filter values → backend query params. `all` = no constraint;
    // `sales` / `cs` / `bd` = department-level (all roles in that dept);
    // everything else maps to a specific `commission_type` so the user
    // can isolate (e.g.) only Team Leader rows.
    const FILTER_TO_QUERY = {
        all: {},
        sales: { department: 'sales' },
        cs: { department: 'cs' },
        bd: { department: 'bd' },
        sales_executive: { commission_type: 'sales_executive' },
        team_leader: { commission_type: 'team_leader' },
        sales_manager: { commission_type: 'sales_manager' },
        cs_agent: { commission_type: 'cs_agent' },
        cs_head: { commission_type: 'cs_head' },
        moa: { commission_type: 'moa' },
        ceo_pool: { commission_type: 'ceo_pool' },
    };

    const fetchTransactions = async (filterVal) => {
        setTxnLoading(true);
        try {
            const params = new URLSearchParams({ month });
            const extra = FILTER_TO_QUERY[filterVal] || {};
            for (const [k, v] of Object.entries(extra)) params.set(k, v);
            const res = await apiClient.get(`/commissions/transactions?${params}`);
            setTransactions(res.data.transactions || []);
            // Reset selection whenever the filter (or month) changes so
            // the user doesn't accidentally bulk-approve a row that's
            // no longer visible.
            setSelectedTxnIds(new Set());
        } catch { toast.error('Failed to load transactions'); }
        finally { setTxnLoading(false); }
    };

    const approveTxn = async (txnId) => {
        try {
            await apiClient.post(`/commissions/transactions/${txnId}/approve`);
            setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, status: 'approved' } : t));
            toast.success('Transaction approved');
        } catch { toast.error('Failed to approve'); }
    };

    const bulkApprove = async (filterVal) => {
        try {
            const body = { month };
            // If caller picked specific rows, approve just those — overrides
            // any visible-filter scope. Otherwise approve every pending row
            // in the currently visible filter.
            if (selectedTxnIds.size > 0) {
                body.transaction_ids = Array.from(selectedTxnIds);
            } else {
                const extra = FILTER_TO_QUERY[filterVal] || {};
                if (extra.department) body.department = extra.department;
                if (extra.commission_type) body.commission_type = extra.commission_type;
            }
            const res = await apiClient.post('/commissions/transactions/bulk-approve', body);
            toast.success(res.data.message);
            setSelectedTxnIds(new Set());
            fetchTransactions(txnFilter);
        } catch { toast.error('Failed to bulk approve'); }
    };

    const toggleTxnSelected = (txnId) => {
        setSelectedTxnIds(prev => {
            const next = new Set(prev);
            if (next.has(txnId)) next.delete(txnId); else next.add(txnId);
            return next;
        });
    };

    const toggleAllVisible = () => {
        // "Select all" only toggles pending rows in the current view —
        // approved ones shouldn't be re-selected.
        const pendingVisible = transactions.filter(t => t.status === 'pending');
        const allSelected = pendingVisible.length > 0 && pendingVisible.every(t => selectedTxnIds.has(t.id));
        if (allSelected) {
            setSelectedTxnIds(new Set());
        } else {
            setSelectedTxnIds(new Set(pendingVisible.map(t => t.id)));
        }
    };

    const saveEditTxn = async () => {
        if (!editingTxn) return;
        try {
            await apiClient.put(`/commissions/transactions/${editingTxn.id}`, {
                final_commission: parseFloat(editCommission),
                ceo_notes: editNotes,
                approve: true,
            });
            setTransactions(prev => prev.map(t => t.id === editingTxn.id ? { ...t, final_commission: parseFloat(editCommission), ceo_notes: editNotes, status: 'approved' } : t));
            setEditingTxn(null);
            toast.success('Commission updated & approved');
        } catch { toast.error('Failed to update'); }
    };

    const generateTransactions = async () => {
        setGenerating(true);
        try {
            await apiClient.post('/commissions/generate-transactions', { month });
            toast.info('Commission generation started. Checking progress...');
            // Poll for completion
            const poll = async () => {
                try {
                    const res = await apiClient.get(`/commissions/generation-status?month=${month}`);
                    if (res.data.status === 'completed') {
                        toast.success(`Generated ${res.data.created || 0} commission transactions`);
                        setGenerating(false);
                        fetchTransactions(txnFilter);
                    } else if (res.data.status === 'failed') {
                        toast.error('Commission generation failed');
                        setGenerating(false);
                    } else {
                        setTimeout(poll, 2000);
                    }
                } catch { setTimeout(poll, 2000); }
            };
            setTimeout(poll, 2000);
        } catch { toast.error('Failed to start generation'); setGenerating(false); }
    };

    if (loading) return <div className="text-center py-12 text-muted-foreground">Loading commission data...</div>;
    if (!data) return null;

    const my = data.my_commission || {};

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="h-6 w-6 text-emerald-500" /> Commission Dashboard</h2>
                    <p className="text-muted-foreground text-sm mt-1">Track earnings, pipeline & benchmarks</p>
                </div>
                <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{monthOpts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
            </div>

            {/* === SALES EXECUTIVE / TEAM LEADER VIEW === */}
            {isSales && (
                <>
                    {/* Approval Status Banner */}
                    {userApprovalStatus === 'approved' ? (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2" data-testid="approval-status-approved">
                            <ShieldCheck className="h-4 w-4" /> Commissions for this month have been <strong>approved by CEO</strong>. Your earned commissions are confirmed.
                        </div>
                    ) : (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2" data-testid="approval-status-pending">
                            <ShieldAlert className="h-4 w-4" /> Commissions for this month are <strong>pending CEO approval</strong>. Earned amounts will be confirmed once approved.
                        </div>
                    )}

                    {/* My Transactions */}
                    {myTxns.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" /> My Transactions ({myTxns.length})</CardTitle>
                                <Button size="sm" variant="outline" onClick={() => openAgentDetail(user.id, user.full_name)} data-testid="view-my-breakdown-btn">
                                    View Full Breakdown (with TL / SM)
                                </Button>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Course</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                        <TableHead className="text-right">Commission</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {myTxns.map(txn => (
                                            <TableRow key={txn.id} className={txn.status === 'approved' ? 'bg-emerald-500/5' : ''}>
                                                <TableCell className="text-sm">{txn.student_name}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">{txn.course_matched}</TableCell>
                                                <TableCell className="text-right text-sm">{fmtCur(txn.amount)}</TableCell>
                                                <TableCell className="text-right font-semibold text-sm">
                                                    {txn.final_commission !== txn.original_commission ? (
                                                        <span><span className="line-through text-muted-foreground mr-1">{fmtCur(txn.original_commission)}</span>{fmtCur(txn.final_commission)}</span>
                                                    ) : fmtCur(txn.final_commission)}
                                                </TableCell>
                                                <TableCell>
                                                    {txn.status === 'approved' ? (
                                                        <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">Approved</Badge>
                                                    ) : (
                                                        <Badge className="bg-amber-500/10 text-amber-600 text-xs">Pending</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {userApprovalStatus === 'approved' ? (
                            <StatCard title="Approved Commission" value={fmtCur(data.approved_commission)} subtitle="CEO approved" icon={ShieldCheck} color="text-emerald-500" bgColor="bg-emerald-500/10" />
                        ) : (
                            <StatCard title="Awaiting Approval" value={fmtCur(data.pending_approval_commission)} subtitle="Pending CEO approval" icon={ShieldAlert} color="text-amber-500" bgColor="bg-amber-500/10" />
                        )}
                        <StatCard title="Pipeline Potential" value={fmtCur(my.pipeline_potential)} subtitle={`${my.pipeline_count || 0} open leads`} icon={Clock} color="text-blue-500" bgColor="bg-blue-500/10" />
                        <StatCard title="Revenue Achieved" value={fmtCur(my.total_revenue)} subtitle={`${my.deals_closed} deals closed`} icon={Target} color="text-blue-500" bgColor="bg-blue-500/10" />
                        {isTL && (
                            userApprovalStatus === 'approved' ? (
                                <StatCard title="TL Commission (Approved)" value={fmtCur(data.approved_tl_commission)} subtitle="From team sales" icon={Award} color="text-purple-500" bgColor="bg-purple-500/10" />
                            ) : (
                                <StatCard title="TL Commission (Pending)" value={fmtCur(data.pending_approval_tl_commission)} subtitle="Awaiting CEO approval" icon={Award} color="text-purple-400" bgColor="bg-purple-400/10" />
                            )
                        )}
                        {!isTL && <StatCard title="18K Benchmark" value={my.benchmark_crossed ? 'Crossed' : `${Math.round(((my.total_revenue || 0) / SALES_BENCHMARK_AED) * 100)}%`} subtitle={my.benchmark_crossed ? 'All commissions unlocked' : `Need ${fmtCur(SALES_BENCHMARK_AED - (my.total_revenue || 0))} more`} icon={Target} color={my.benchmark_crossed ? "text-emerald-500" : "text-red-500"} bgColor={my.benchmark_crossed ? "bg-emerald-500/10" : "bg-red-500/10"} />}
                    </div>

                    {!my.benchmark_crossed && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-600 dark:text-amber-400">
                            <strong>Benchmark Alert:</strong> You need {fmtCur(18000 - (my.total_revenue || 0))} more in revenue to unlock your earned commissions. Keep pushing!
                        </div>
                    )}

                    {/* TL Team View */}
                    {isTL && data.team_commissions?.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Team Commission Breakdown</CardTitle></CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader><TableRow><TableHead>Agent</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Deals</TableHead><TableHead className="text-right">Earned</TableHead><TableHead className="text-right">Pending</TableHead><TableHead className="text-right">Pipeline Potential</TableHead><TableHead>Benchmark</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                        {data.team_commissions.map((t, i) => (
                                            <TableRow key={i}>
                                                <TableCell className="font-medium">
                                                    <button
                                                        onClick={() => openAgentDetail(t.agent_id || t.id, t.agent_name)}
                                                        className="text-primary hover:underline text-left"
                                                        data-testid={`sales-team-agent-${i}`}
                                                    >
                                                        {t.agent_name}
                                                    </button>
                                                </TableCell>
                                                <TableCell className="text-right font-mono">{fmtCur(t.total_revenue)}</TableCell>
                                                <TableCell className="text-right">{t.deals_closed}</TableCell>
                                                <TableCell className="text-right font-mono text-emerald-500">{fmtCur(t.earned_commission)}</TableCell>
                                                <TableCell className="text-right font-mono text-amber-500">{fmtCur(t.pending_commission)}</TableCell>
                                                <TableCell className="text-right font-mono text-blue-500">{fmtCur(t.pipeline_potential)}</TableCell>
                                                <TableCell>{t.benchmark_crossed ? <Badge className="bg-emerald-500 text-white text-xs">Crossed</Badge> : <Badge variant="outline" className="text-xs text-red-500">Below 18K</Badge>}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* === CS AGENT / CS HEAD VIEW === */}
            {isCS && (
                <>
                    {/* Approval Status Banner */}
                    {userApprovalStatus === 'approved' ? (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2" data-testid="cs-approval-status-approved">
                            <ShieldCheck className="h-4 w-4" /> Commissions for this month have been <strong>approved by CEO</strong>. Your earned commissions are confirmed.
                        </div>
                    ) : (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2" data-testid="cs-approval-status-pending">
                            <ShieldAlert className="h-4 w-4" /> Commissions for this month are <strong>pending CEO approval</strong>. Earned amounts will be confirmed once approved.
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {userApprovalStatus === 'approved' ? (
                            <StatCard title="Approved Commission" value={fmtCur(data.approved_commission)} subtitle={`${my.upgrades_closed} upgrades closed`} icon={ShieldCheck} color="text-emerald-500" bgColor="bg-emerald-500/10" />
                        ) : (
                            <StatCard title="Awaiting Approval" value={fmtCur(data.pending_approval_commission)} subtitle={`${my.upgrades_closed} upgrades closed`} icon={ShieldAlert} color="text-amber-500" bgColor="bg-amber-500/10" />
                        )}
                        <StatCard title="Pipeline Pending" value={fmtCur(my.pending_commission)} subtitle={`${my.pipeline_count} in pipeline`} icon={Clock} color="text-orange-500" bgColor="bg-orange-500/10" />
                        {isCSHead && (
                            userApprovalStatus === 'approved' ? (
                                <StatCard title="CS Head (Approved)" value={fmtCur(data.approved_cs_head_commission)} subtitle="From all team upgrades" icon={Award} color="text-purple-500" bgColor="bg-purple-500/10" />
                            ) : (
                                <StatCard title="CS Head (Pending)" value={fmtCur(data.pending_approval_cs_head_commission)} subtitle="Awaiting CEO approval" icon={Award} color="text-purple-400" bgColor="bg-purple-400/10" />
                            )
                        )}
                        {!isCSHead && <StatCard title="Upgrades Closed" value={my.upgrades_closed} subtitle="This month" icon={TrendingUp} color="text-blue-500" bgColor="bg-blue-500/10" />}
                        {!isCSHead && <StatCard title="Pipeline" value={my.pipeline_count} subtitle="Pending upgrades" icon={ArrowUpRight} color="text-orange-500" bgColor="bg-orange-500/10" />}
                    </div>

                    {isCSHead && data.team_commissions?.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> CS Team Commission</CardTitle></CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader><TableRow><TableHead>Agent</TableHead><TableHead className="text-right">Upgrades</TableHead><TableHead className="text-right">Earned</TableHead><TableHead className="text-right">Pending</TableHead><TableHead className="text-right">Pipeline</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                        {data.team_commissions.map((t, i) => (
                                            <TableRow key={i}>
                                                <TableCell className="font-medium">
                                                    <button
                                                        onClick={() => openAgentDetail(t.agent_id || t.id, t.agent_name)}
                                                        className="text-primary hover:underline text-left"
                                                        data-testid={`cs-team-agent-${i}`}
                                                    >
                                                        {t.agent_name}
                                                    </button>
                                                </TableCell>
                                                <TableCell className="text-right">{t.upgrades_closed}</TableCell>
                                                <TableCell className="text-right font-mono text-emerald-500">{fmtCur(t.earned_commission)}</TableCell>
                                                <TableCell className="text-right font-mono text-amber-500">{fmtCur(t.pending_commission)}</TableCell>
                                                <TableCell className="text-right">{t.pipeline_count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* === BD AGENT VIEW === */}
            {isBD && (
                <>
                    {userApprovalStatus === 'approved' ? (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2" data-testid="bd-approval-approved">
                            <ShieldCheck className="h-4 w-4" /> BD Commissions for this month have been <strong>approved by CEO</strong>.
                        </div>
                    ) : (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2" data-testid="bd-approval-pending">
                            <ShieldAlert className="h-4 w-4" /> BD Commissions for this month are <strong>pending CEO approval</strong>.
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {userApprovalStatus === 'approved' ? (
                            <StatCard title="Approved Commission" value={fmtCur(data.approved_commission)} subtitle="CEO approved" icon={ShieldCheck} color="text-emerald-500" bgColor="bg-emerald-500/10" />
                        ) : (
                            <StatCard title="Awaiting Approval" value={fmtCur(data.pending_approval_commission)} subtitle="Pending CEO approval" icon={ShieldAlert} color="text-amber-500" bgColor="bg-amber-500/10" />
                        )}
                        <StatCard title="Gross Deposits" value={fmtCur(my.gross_deposits)} subtitle={`${my.deposit_count || 0} deposits`} icon={Target} color="text-blue-500" bgColor="bg-blue-500/10" />
                        <StatCard title="1% Flat" value={fmtCur(my.flat_commission)} subtitle="On gross deposits" icon={TrendingUp} color="text-cyan-500" bgColor="bg-cyan-500/10" />
                        <StatCard title="1% Net" value={fmtCur(my.net_commission)} subtitle={`Net: ${fmtCur(my.net_amount)}`} icon={Award} color="text-purple-500" bgColor="bg-purple-500/10" />
                    </div>
                    {my.earned_details?.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" /> BD Transaction Details</CardTitle></CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Student</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">1% Flat</TableHead><TableHead>Date</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {my.earned_details.map((d, i) => (
                                            <TableRow key={`bd-${i}`}>
                                                <TableCell className="text-sm font-medium">{d.student_name}</TableCell>
                                                <TableCell><Badge variant="outline" className="text-xs capitalize">{d.type}</Badge></TableCell>
                                                <TableCell className="text-right font-mono text-sm">{fmtCur(d.amount)}</TableCell>
                                                <TableCell className="text-right font-mono text-emerald-500 text-sm">{fmtCur(d.flat_1pct)}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">{d.date}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* === PAYOUT SUMMARY — All non-CEO roles === */}
            {!isCEO && payoutData && (
                <Card className="border-primary/20" data-testid="payout-summary-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> Monthly Payout Summary — {monthOpts.find(o => o.value === month)?.label}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                            <div className="text-center p-3 rounded-lg bg-muted/50">
                                <p className="text-xs text-muted-foreground">Gross Salary</p>
                                <p className="text-lg font-bold font-mono">{fmtCur(payoutData.gross_salary)}</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-red-500/5">
                                <p className="text-xs text-muted-foreground">LOP Deduction</p>
                                <p className="text-lg font-bold font-mono text-red-500">-{fmtCur(payoutData.lop_deduction)}</p>
                                <p className="text-[10px] text-muted-foreground">{payoutData.lop_days} days</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-emerald-500/5">
                                <p className="text-xs text-muted-foreground">Commission</p>
                                <p className="text-lg font-bold font-mono text-emerald-500">+{fmtCur(payoutData.commission_earned)}</p>
                                <Badge variant="outline" className={`text-[10px] mt-0.5 ${payoutData.commission_status === 'approved' ? 'text-emerald-600 border-emerald-500/30' : 'text-amber-600 border-amber-500/30'}`}>
                                    {payoutData.commission_status}
                                </Badge>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-blue-500/5">
                                <p className="text-xs text-muted-foreground">Bonus</p>
                                <p className="text-lg font-bold font-mono text-blue-500">+{fmtCur(payoutData.bonus)}</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/20">
                                <p className="text-xs text-muted-foreground font-medium">Total Payout</p>
                                <p className="text-xl font-bold font-mono text-primary">{fmtCur(payoutData.net_payout)}</p>
                            </div>
                        </div>
                        {payoutData.commission_status !== 'approved' && payoutData.commission_earned > 0 && (
                            <p className="text-xs text-muted-foreground text-center">
                                Once commissions are approved, your total payout will be <strong>{fmtCur(payoutData.gross_salary - payoutData.lop_deduction + payoutData.commission_earned + payoutData.bonus)}</strong>
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}
            {isCEO && (
                <Tabs defaultValue="sales">
                    <TabsList>
                        <TabsTrigger value="sales">Sales Commissions</TabsTrigger>
                        <TabsTrigger value="cs">CS Commissions</TabsTrigger>
                        <TabsTrigger value="bd">BD Commissions</TabsTrigger>
                        <TabsTrigger value="transactions" onClick={() => fetchTransactions(txnFilter)}>Approve Transactions</TabsTrigger>
                        <TabsTrigger value="pools">CEO Pools</TabsTrigger>
                    </TabsList>

                    <TabsContent value="sales" className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard title="Sales Earned" value={fmtCur(data.total_sales_earned)} icon={CheckCircle} color="text-emerald-500" bgColor="bg-emerald-500/10" />
                            <StatCard title="Sales Pending" value={fmtCur(data.total_sales_pending)} icon={Clock} color="text-amber-500" bgColor="bg-amber-500/10" />
                            <StatCard title="TL Earned" value={fmtCur(data.total_tl_earned)} icon={Award} color="text-purple-500" bgColor="bg-purple-500/10" />
                            <StatCard title="SM / CEO Pool" value={fmtCur(data.sm_bonus_pool)} icon={DollarSign} color="text-blue-500" bgColor="bg-blue-500/10" />
                        </div>
                        {/* CEO Approval Control */}
                        <div className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid="ceo-sales-approval-control">
                            <div className="flex items-center gap-2">
                                {getApprovalStatus('sales') ? (
                                    <><ShieldCheck className="h-5 w-5 text-emerald-500" /><span className="text-sm font-medium text-emerald-600">Sales commissions approved for {monthOpts.find(o => o.value === month)?.label}</span></>
                                ) : (
                                    <><ShieldAlert className="h-5 w-5 text-amber-500" /><span className="text-sm font-medium text-amber-600">Sales commissions pending approval</span></>
                                )}
                            </div>
                            {getApprovalStatus('sales') ? (
                                <Button variant="outline" size="sm" onClick={() => handleApproval('sales', 'revoke')} disabled={approving} data-testid="revoke-sales-btn" className="text-red-500 border-red-500/30 hover:bg-red-500/10">
                                    Revoke Approval
                                </Button>
                            ) : (
                                <Button size="sm" onClick={() => handleApproval('sales', 'approve')} disabled={approving} data-testid="approve-sales-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                                    <ShieldCheck className="h-4 w-4 mr-1" /> Approve Sales Commissions
                                </Button>
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openDrill('sales')} data-testid="drill-sales-commission">
                            <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> View Full Sales Commission Table
                        </Button>
                    </TabsContent>

                    <TabsContent value="cs" className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard title="CS Earned" value={fmtCur(data.total_cs_earned)} icon={CheckCircle} color="text-emerald-500" bgColor="bg-emerald-500/10" />
                            <StatCard title="CS Pending" value={fmtCur(data.total_cs_pending)} icon={Clock} color="text-amber-500" bgColor="bg-amber-500/10" />
                            <StatCard title="CS Head Earned" value={fmtCur(data.total_cs_head_earned)} icon={Award} color="text-purple-500" bgColor="bg-purple-500/10" />
                            <StatCard title="Mentor Pool" value={fmtCur(data.mentor_pool)} subtitle="Edwin — Cash bonus (confidential)" icon={DollarSign} color="text-red-500" bgColor="bg-red-500/10" />
                        </div>
                        {/* CEO Approval Control */}
                        <div className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid="ceo-cs-approval-control">
                            <div className="flex items-center gap-2">
                                {getApprovalStatus('cs') ? (
                                    <><ShieldCheck className="h-5 w-5 text-emerald-500" /><span className="text-sm font-medium text-emerald-600">CS commissions approved for {monthOpts.find(o => o.value === month)?.label}</span></>
                                ) : (
                                    <><ShieldAlert className="h-5 w-5 text-amber-500" /><span className="text-sm font-medium text-amber-600">CS commissions pending approval</span></>
                                )}
                            </div>
                            {getApprovalStatus('cs') ? (
                                <Button variant="outline" size="sm" onClick={() => handleApproval('cs', 'revoke')} disabled={approving} data-testid="revoke-cs-btn" className="text-red-500 border-red-500/30 hover:bg-red-500/10">
                                    Revoke Approval
                                </Button>
                            ) : (
                                <Button size="sm" onClick={() => handleApproval('cs', 'approve')} disabled={approving} data-testid="approve-cs-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                                    <ShieldCheck className="h-4 w-4 mr-1" /> Approve CS Commissions
                                </Button>
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openDrill('cs')} data-testid="drill-cs-commission">
                            <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> View Full CS Commission Table
                        </Button>
                    </TabsContent>

                    {/* === BD COMMISSIONS TAB === */}
                    <TabsContent value="bd" className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <StatCard title="BD Total Earned" value={fmtCur(data.total_bd_earned)} subtitle={`${(data.bd_commissions || []).length} BD agents`} icon={CheckCircle} color="text-emerald-500" bgColor="bg-emerald-500/10" />
                            <StatCard title="Total BD Deposits" value={fmtCur(data.total_bd_deposits)} subtitle="Gross deposits this month" icon={Target} color="text-blue-500" bgColor="bg-blue-500/10" />
                            <StatCard title="Commission Rate" value="2%" subtitle="1% flat + 1% net" icon={Award} color="text-purple-500" bgColor="bg-purple-500/10" />
                        </div>
                        {/* CEO Approval Control for BD */}
                        <div className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid="ceo-bd-approval-control">
                            <div className="flex items-center gap-2">
                                {getApprovalStatus('bd') ? (
                                    <><ShieldCheck className="h-5 w-5 text-emerald-500" /><span className="text-sm font-medium text-emerald-600">BD commissions approved for {monthOpts.find(o => o.value === month)?.label}</span></>
                                ) : (
                                    <><ShieldAlert className="h-5 w-5 text-amber-500" /><span className="text-sm font-medium text-amber-600">BD commissions pending approval</span></>
                                )}
                            </div>
                            {getApprovalStatus('bd') ? (
                                <Button variant="outline" size="sm" onClick={() => handleApproval('bd', 'revoke')} disabled={approving} data-testid="revoke-bd-btn" className="text-red-500 border-red-500/30 hover:bg-red-500/10">
                                    Revoke Approval
                                </Button>
                            ) : (
                                <Button size="sm" onClick={() => handleApproval('bd', 'approve')} disabled={approving} data-testid="approve-bd-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                                    <ShieldCheck className="h-4 w-4 mr-1" /> Approve BD Commissions
                                </Button>
                            )}
                        </div>
                        {/* BD Agent Table */}
                        {data.bd_commissions?.length > 0 && (
                            <Card>
                                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> BD Agent Commission Breakdown</CardTitle></CardHeader>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader><TableRow>
                                            <TableHead>Agent</TableHead>
                                            <TableHead className="text-right">Gross Deposits</TableHead>
                                            <TableHead className="text-right">Withdrawals</TableHead>
                                            <TableHead className="text-right">Net</TableHead>
                                            <TableHead className="text-right">1% Flat</TableHead>
                                            <TableHead className="text-right">1% Net</TableHead>
                                            <TableHead className="text-right">Total Commission</TableHead>
                                        </TableRow></TableHeader>
                                        <TableBody>
                                            {data.bd_commissions.map((b, i) => (
                                                <TableRow key={b.agent_id || i}>
                                                    <TableCell className="font-medium">
                                                        <button
                                                            onClick={() => openAgentDetail(b.agent_id, b.agent_name)}
                                                            className="text-primary hover:underline text-left"
                                                            data-testid={`bd-agent-${i}`}
                                                        >
                                                            {b.agent_name}
                                                        </button>
                                                    </TableCell>
                                                    <TableCell className="text-right font-mono">{fmtCur(b.gross_deposits)}</TableCell>
                                                    <TableCell className="text-right font-mono text-red-500">{fmtCur(b.total_withdrawals)}</TableCell>
                                                    <TableCell className="text-right font-mono">{fmtCur(b.net_amount)}</TableCell>
                                                    <TableCell className="text-right font-mono text-cyan-500">{fmtCur(b.flat_commission)}</TableCell>
                                                    <TableCell className="text-right font-mono text-purple-500">{fmtCur(b.net_commission)}</TableCell>
                                                    <TableCell className="text-right font-mono text-emerald-500 font-bold">{fmtCur(b.earned_commission)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow className="border-t-2 font-bold">
                                                <TableCell>Total</TableCell>
                                                <TableCell className="text-right font-mono">{fmtCur(data.total_bd_deposits)}</TableCell>
                                                <TableCell className="text-right font-mono text-red-500">{fmtCur((data.bd_commissions || []).reduce((s, b) => s + (b.total_withdrawals || 0), 0))}</TableCell>
                                                <TableCell className="text-right font-mono">{fmtCur((data.bd_commissions || []).reduce((s, b) => s + (b.net_amount || 0), 0))}</TableCell>
                                                <TableCell className="text-right font-mono text-cyan-500">{fmtCur((data.bd_commissions || []).reduce((s, b) => s + (b.flat_commission || 0), 0))}</TableCell>
                                                <TableCell className="text-right font-mono text-purple-500">{fmtCur((data.bd_commissions || []).reduce((s, b) => s + (b.net_commission || 0), 0))}</TableCell>
                                                <TableCell className="text-right font-mono text-emerald-500">{fmtCur(data.total_bd_earned)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>


                    {/* === TRANSACTIONS APPROVAL TAB === */}
                    <TabsContent value="transactions" className="space-y-4 mt-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Select value={txnFilter} onValueChange={(v) => { setTxnFilter(v); fetchTransactions(v); }}>
                                    <SelectTrigger className="w-[200px] h-8" data-testid="txn-dept-filter"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Transactions</SelectItem>
                                        <SelectItem value="sales">All Sales</SelectItem>
                                        <SelectItem value="cs">All Customer Service</SelectItem>
                                        <SelectItem value="bd">All Business Development</SelectItem>
                                        <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">By Role</div>
                                        <SelectItem value="sales_executive">Sales Executive</SelectItem>
                                        <SelectItem value="team_leader">Team Leader</SelectItem>
                                        <SelectItem value="sales_manager">Sales Manager</SelectItem>
                                        <SelectItem value="cs_agent">Academic Counsellor (CS)</SelectItem>
                                        <SelectItem value="cs_head">CS Head</SelectItem>
                                        <SelectItem value="moa">Master of Academics</SelectItem>
                                        <SelectItem value="ceo_pool">CEO Pool</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Badge variant="outline" className="text-xs">{pendingCount} pending</Badge>
                                <Badge variant="outline" className="text-xs text-emerald-600">{approvedCount} approved</Badge>
                                {selectedTxnIds.size > 0 && (
                                    <Badge className="bg-blue-500/10 text-blue-600 text-xs" data-testid="selection-count">
                                        {selectedTxnIds.size} selected
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={generateTransactions} disabled={generating} data-testid="generate-txns-btn">
                                    {generating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null} Generate Transactions
                                </Button>
                                <Button size="sm" onClick={() => bulkApprove(txnFilter)}
                                    disabled={selectedTxnIds.size === 0 && !transactions.some(t => t.status === 'pending')}
                                    data-testid="bulk-approve-btn"
                                    className="bg-emerald-500 hover:bg-emerald-600 text-white">
                                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                                    {selectedTxnIds.size > 0 ? `Approve ${selectedTxnIds.size} Selected` : 'Approve All Pending'}
                                </Button>
                            </div>
                        </div>

                        {txnLoading ? (
                            <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading transactions...</div>
                        ) : transactions.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">No transactions found. Click "Generate Transactions" to scan enrolled leads and CS upgrades.</div>
                        ) : (
                            <Card>
                                <CardContent className="p-0">
                                    <div className="max-h-[500px] overflow-auto">
                                        <Table>
                                            <TableHeader className="sticky top-0 bg-card z-10">
                                                <TableRow>
                                                    <TableHead className="w-[36px]">
                                                        <input
                                                            type="checkbox"
                                                            data-testid="select-all-pending"
                                                            checked={
                                                                transactions.some(t => t.status === 'pending') &&
                                                                transactions.filter(t => t.status === 'pending').every(t => selectedTxnIds.has(t.id))
                                                            }
                                                            onChange={toggleAllVisible}
                                                            className="h-3.5 w-3.5 cursor-pointer"
                                                            aria-label="Select all pending"
                                                        />
                                                    </TableHead>
                                                    <TableHead className="w-[140px]">Agent</TableHead>
                                                    <TableHead className="w-[110px]">Role</TableHead>
                                                    <TableHead>Student</TableHead>
                                                    <TableHead className="text-right w-[90px]">Amount</TableHead>
                                                    <TableHead>Course</TableHead>
                                                    <TableHead className="text-right w-[100px]">Commission</TableHead>
                                                    <TableHead className="w-[80px]">Status</TableHead>
                                                    <TableHead className="text-right w-[130px]">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {transactions.map(txn => (
                                                    <TableRow key={txn.id} className={txn.status === 'approved' ? 'bg-emerald-500/5' : selectedTxnIds.has(txn.id) ? 'bg-blue-500/5' : ''}>
                                                        <TableCell>
                                                            <input
                                                                type="checkbox"
                                                                data-testid={`select-txn-${txn.id}`}
                                                                checked={selectedTxnIds.has(txn.id)}
                                                                disabled={txn.status !== 'pending'}
                                                                onChange={() => toggleTxnSelected(txn.id)}
                                                                className="h-3.5 w-3.5 cursor-pointer disabled:opacity-30"
                                                                aria-label="Select transaction"
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-medium text-sm">{txn.agent_name}</TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className="text-xs capitalize">
                                                                {({
                                                                    sales_executive: 'SE',
                                                                    team_leader: 'TL',
                                                                    sales_manager: 'SM',
                                                                    cs_agent: 'CS',
                                                                    cs_head: 'CS Head',
                                                                    moa: 'MoA',
                                                                    ceo_pool: 'CEO Pool',
                                                                    bd_agent: 'BD',
                                                                })[txn.commission_type] || txn.commission_type || '—'}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-sm">{txn.student_name}{txn.from_agent ? <span className="text-xs text-muted-foreground ml-1">(via {txn.from_agent})</span> : null}</TableCell>
                                                        <TableCell className="text-right text-sm">{fmtCur(txn.amount)}</TableCell>
                                                        <TableCell className="text-xs text-muted-foreground">{txn.course_matched}</TableCell>
                                                        <TableCell className="text-right font-semibold text-sm">
                                                            {txn.final_commission !== txn.original_commission ? (
                                                                <span><span className="line-through text-muted-foreground mr-1">{fmtCur(txn.original_commission)}</span>{fmtCur(txn.final_commission)}</span>
                                                            ) : fmtCur(txn.final_commission)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {txn.status === 'approved' ? (
                                                                <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">Approved</Badge>
                                                            ) : (
                                                                <Badge className="bg-amber-500/10 text-amber-600 text-xs">Pending</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <div className="flex items-center justify-end gap-1">
                                                                {txn.status === 'pending' && (
                                                                    <>
                                                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:bg-emerald-500/10" onClick={() => approveTxn(txn.id)} data-testid={`approve-txn-${txn.id}`} title="Approve">
                                                                            <Check className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:bg-blue-500/10" onClick={() => { setEditingTxn(txn); setEditCommission(String(txn.final_commission)); setEditNotes(txn.ceo_notes || ''); }} data-testid={`edit-txn-${txn.id}`} title="Edit & Approve">
                                                                            <Pencil className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                {txn.ceo_notes && <span className="text-xs text-muted-foreground" title={txn.ceo_notes}>*</span>}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    <TabsContent value="pools" className="space-y-4 mt-4">
                        {ceoPool && (
                            <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-transparent">
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div>
                                            <p className="text-xs uppercase tracking-wide text-muted-foreground">CEO Pool · {monthOpts.find(o => o.value === month)?.label}</p>
                                            <p className="text-4xl font-bold font-mono text-amber-600 mt-1" data-testid="ceo-pool-total">{fmtCur(ceoPool.total_ceo_pool)}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Course pool slice on every sale &amp; upgrade + stranded SE commissions
                                                (SEs below the AED {ceoPool.benchmark_aed?.toLocaleString()} threshold this month).
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 text-center text-xs min-w-[420px]">
                                            <div className="rounded-lg border bg-card p-3" data-testid="ceo-pool-sales-card">
                                                <p className="text-muted-foreground">Sales course-pool</p>
                                                <p className="font-mono font-semibold mt-1">{fmtCur(ceoPool.breakdown?.course_pool_sales?.amount)}</p>
                                                <p className="text-muted-foreground mt-0.5">{ceoPool.breakdown?.course_pool_sales?.deals} deals</p>
                                            </div>
                                            <div className="rounded-lg border bg-card p-3" data-testid="ceo-pool-upgrade-card">
                                                <p className="text-muted-foreground">Upgrade course-pool</p>
                                                <p className="font-mono font-semibold mt-1">{fmtCur(ceoPool.breakdown?.course_pool_upgrades?.amount)}</p>
                                                <p className="text-muted-foreground mt-0.5">{ceoPool.breakdown?.course_pool_upgrades?.deals} deals</p>
                                            </div>
                                            <div className="rounded-lg border bg-card p-3" data-testid="ceo-pool-stranded-card">
                                                <p className="text-muted-foreground">Stranded SE</p>
                                                <p className="font-mono font-semibold mt-1">{fmtCur(ceoPool.breakdown?.stranded_se_commissions?.amount)}</p>
                                                <p className="text-muted-foreground mt-0.5">{ceoPool.breakdown?.stranded_se_commissions?.agents_count} agents</p>
                                            </div>
                                        </div>
                                    </div>

                                    {ceoPool.stranded_agents?.length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                                Sub-threshold Sales Executives (commissions forfeited to CEO Pool)
                                            </p>
                                            <div className="rounded-lg border overflow-hidden">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                                                        <tr>
                                                            <th className="text-left px-3 py-2">Agent</th>
                                                            <th className="text-right px-3 py-2">Revenue</th>
                                                            <th className="text-right px-3 py-2">Shortfall</th>
                                                            <th className="text-right px-3 py-2">Deals</th>
                                                            <th className="text-right px-3 py-2">→ CEO Pool</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {ceoPool.stranded_agents.map((a) => (
                                                            <tr key={a.agent_id} className="border-t hover:bg-muted/30" data-testid={`stranded-${a.agent_id}`}>
                                                                <td className="px-3 py-2 font-medium">{a.agent_name}</td>
                                                                <td className="px-3 py-2 text-right font-mono text-xs">{fmtCur(a.achieved_revenue)}</td>
                                                                <td className="px-3 py-2 text-right font-mono text-xs text-amber-600">{fmtCur(a.shortfall)}</td>
                                                                <td className="px-3 py-2 text-right text-xs">{a.deals_closed}</td>
                                                                <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">{fmtCur(a.stranded_commission)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card className="border-blue-500/30">
                                <CardContent className="p-5 text-center">
                                    <p className="text-xs text-muted-foreground uppercase">SM / CEO Bonus Pool</p>
                                    <p className="text-3xl font-bold font-mono text-blue-500 mt-2">{fmtCur(data.sm_bonus_pool)}</p>
                                    <p className="text-xs text-muted-foreground mt-1">From all sales this month</p>
                                </CardContent>
                            </Card>
                            <Card className="border-red-500/30">
                                <CardContent className="p-5 text-center">
                                    <p className="text-xs text-muted-foreground uppercase">Mentor Pool (Edwin)</p>
                                    <p className="text-3xl font-bold font-mono text-red-500 mt-2">{fmtCur(data.mentor_pool)}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Cash bonus — confidential</p>
                                </CardContent>
                            </Card>
                            <Card className="border-emerald-500/30">
                                <CardContent className="p-5 text-center">
                                    <p className="text-xs text-muted-foreground uppercase">Total Commission Outflow</p>
                                    <p className="text-3xl font-bold font-mono text-emerald-500 mt-2">{fmtCur((data.total_sales_earned || 0) + (data.total_tl_earned || 0) + (data.total_cs_earned || 0) + (data.total_cs_head_earned || 0) + (data.total_bd_earned || 0))}</p>
                                    <p className="text-xs text-muted-foreground mt-1">All earned commissions combined</p>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            )}

            {/* Scatter Chart */}
            {scatterData && <ScatterChart data={scatterData.data} agentName={scatterData.agent_name} />}

            {/* Drill-Down Dialog */}
            <Dialog open={!!drillDept} onOpenChange={() => setDrillDept(null)}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{drillDept === 'sales' ? 'Sales' : 'CS'} Commission Breakdown — {monthOpts.find(o => o.value === month)?.label}</DialogTitle>
                    </DialogHeader>
                    {drillLoading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> : drillData && (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">Sr</TableHead>
                                    <TableHead>{drillDept === 'sales' ? 'Sales Executive' : 'CS Agent'}</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead className="text-right">{drillDept === 'sales' ? 'Achieved' : 'Upgrades'}</TableHead>
                                    <TableHead className="text-right">Earned</TableHead>
                                    <TableHead className="text-right">Pending</TableHead>
                                    {drillDept === 'sales' && <TableHead className="text-right">Pipeline<br/>Potential</TableHead>}
                                    {drillDept === 'sales' && <TableHead>Benchmark</TableHead>}
                                    {drillDept === 'sales' && <TableHead className="text-right">TL Comm</TableHead>}
                                    {drillDept === 'sales' && <TableHead className="text-right">SM Pool</TableHead>}
                                    {drillDept === 'cs' && <TableHead className="text-right">CS Head</TableHead>}
                                    {drillDept === 'cs' && <TableHead className="text-right">Mentor</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {drillData.rows?.map((r) => (
                                    <TableRow key={r.sr}>
                                        <TableCell className="text-muted-foreground">{r.sr}</TableCell>
                                        <TableCell className="font-medium">
                                            <button
                                                onClick={() => openAgentDetail(r.agent_id || r.id, r.name)}
                                                className="text-primary hover:underline text-left"
                                                data-testid={`drill-agent-${r.sr}`}
                                            >
                                                {r.name}
                                            </button>
                                        </TableCell>
                                        <TableCell><Badge variant="outline" className="text-xs">{r.role}</Badge></TableCell>
                                        <TableCell className="text-right font-mono">{drillDept === 'sales' ? fmtCur(r.achieved) : r.upgrades}</TableCell>
                                        <TableCell className="text-right font-mono text-emerald-500">{fmtCur(r.earned_commission)}</TableCell>
                                        <TableCell className="text-right font-mono text-amber-500">{fmtCur(r.pending_commission)}</TableCell>
                                        {drillDept === 'sales' && (
                                            <TableCell className="text-right font-mono text-blue-500" title={`${r.pipeline_count || 0} open leads`}>
                                                {fmtCur(r.pipeline_potential)}
                                                {r.pipeline_count > 0 && <span className="block text-[10px] text-muted-foreground">{r.pipeline_count} leads</span>}
                                            </TableCell>
                                        )}
                                        {drillDept === 'sales' && <TableCell>{r.benchmark_crossed ? <Badge className="bg-emerald-500 text-white text-xs">Yes</Badge> : <Badge variant="outline" className="text-xs text-red-500">No</Badge>}</TableCell>}
                                        {drillDept === 'sales' && <TableCell className="text-right font-mono text-purple-500">{fmtCur(r.tl_commission)}</TableCell>}
                                        {drillDept === 'sales' && <TableCell className="text-right font-mono text-blue-500">{fmtCur(r.sm_pool)}</TableCell>}
                                        {drillDept === 'cs' && <TableCell className="text-right font-mono text-purple-500">{fmtCur(r.cs_head_commission)}</TableCell>}
                                        {drillDept === 'cs' && <TableCell className="text-right font-mono text-red-500">{fmtCur(r.mentor_commission)}</TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>

            {/* Agent Commission Details Dialog */}
            <Dialog open={!!agentDetail} onOpenChange={() => { setAgentDetail(null); setAgentDetailData(null); }}>
                <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-primary" />
                            {agentDetail?.agent_name || 'Agent'} — Commission Breakdown
                            <Badge variant="outline" className="ml-2 text-xs">{monthOpts.find(o => o.value === month)?.label}</Badge>
                        </DialogTitle>
                    </DialogHeader>
                    {agentDetailLoading ? (
                        <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading details...</div>
                    ) : agentDetailData ? (
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                                {agentDetailData.team_name && <span><strong className="text-foreground">Team:</strong> {agentDetailData.team_name}</span>}
                                {agentDetailData.tl_name && <span><strong className="text-foreground">TL:</strong> {agentDetailData.tl_name}</span>}
                                {agentDetailData.sm_name && <span><strong className="text-foreground">SM:</strong> {agentDetailData.sm_name}</span>}
                                <span><strong className="text-foreground">Customers closed:</strong> {agentDetailData.totals?.count || 0}</span>
                            </div>
                            {agentDetailData.rows?.length === 0 ? (
                                <div className="text-center py-10 text-muted-foreground text-sm">
                                    No closed customers found for {agentDetailData.agent_name} in this month.
                                </div>
                            ) : (
                                <div className="border rounded-lg overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-10">Sr No</TableHead>
                                                <TableHead>Name of customer</TableHead>
                                                <TableHead>Course</TableHead>
                                                <TableHead className="text-right">Amount</TableHead>
                                                <TableHead className="text-right">Earned commission</TableHead>
                                                <TableHead>TL</TableHead>
                                                <TableHead>SM</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {agentDetailData.rows.map((r) => (
                                                <TableRow key={r.sr} data-testid={`agent-detail-row-${r.sr}`}>
                                                    <TableCell className="text-muted-foreground">{r.sr}</TableCell>
                                                    <TableCell className="font-medium text-sm">{r.customer_name || '—'}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">{r.course || '—'}</TableCell>
                                                    <TableCell className="text-right font-mono text-sm">{fmtCur(r.amount)}</TableCell>
                                                    <TableCell className="text-right font-mono text-sm text-emerald-500 font-semibold">{fmtCur(r.earned_commission)}</TableCell>
                                                    <TableCell className="text-xs">{r.tl}</TableCell>
                                                    <TableCell className="text-xs">{r.sm}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow className="border-t-2 font-bold bg-muted/30">
                                                <TableCell colSpan={3} className="text-right">Total</TableCell>
                                                <TableCell className="text-right font-mono">{fmtCur(agentDetailData.totals?.amount)}</TableCell>
                                                <TableCell className="text-right font-mono text-emerald-600">{fmtCur(agentDetailData.totals?.earned_commission)}</TableCell>
                                                <TableCell colSpan={2}></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Edit Commission Dialog */}
            <Dialog open={!!editingTxn} onOpenChange={() => setEditingTxn(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Edit Commission</DialogTitle></DialogHeader>
                    {editingTxn && (
                        <div className="space-y-4">
                            <div className="text-sm space-y-1">
                                <p><strong>Agent:</strong> {editingTxn.agent_name}</p>
                                <p><strong>Student:</strong> {editingTxn.student_name}</p>
                                <p><strong>Amount:</strong> {fmtCur(editingTxn.amount)}</p>
                                <p><strong>Original Commission:</strong> {fmtCur(editingTxn.original_commission)}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium">New Commission Amount (AED)</label>
                                <Input type="number" value={editCommission} onChange={e => setEditCommission(e.target.value)} data-testid="edit-commission-input" className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">CEO Notes</label>
                                <Input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Reason for adjustment..." data-testid="edit-notes-input" className="mt-1" />
                            </div>
                            <div className="flex gap-2 justify-end">
                                <Button variant="outline" size="sm" onClick={() => setEditingTxn(null)}>Cancel</Button>
                                <Button size="sm" onClick={saveEditTxn} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-edit-txn">
                                    <Check className="h-3.5 w-3.5 mr-1" /> Save & Approve
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

const SALES_BENCHMARK_AED = 18000;
